import { History } from 'history';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { ErrorMessagesProvider } from '../../../web/components/errorMessages/ErrorMessagesProvider';
import { PrivateRoute, RouteWithLoggedPageView } from '../../../web/components/PrivateRoute';
import { CustomerSessionStart } from '../../../web/containers/CustomerSessionStart';
import { LazyLoader } from '../../../web/containers/LazyLoader';
import { Logout } from '../../../web/containers/Logout';
import { bs } from '../../../web/styles/styles';
import { MainContainerErrorBoundary } from '../../components/MainContainerErrorBoundary';
import { WsNotificationProvider } from '../../components/WsNotification';
import { CombinedProviders } from '../../contexts/CombineProviders';
import { TelephonyContextProvider } from '../../contexts/telephony';
import { TysonModeProvider } from '../../contexts/tysonMode';
import { TranscriptionEventsProvider } from '../../entities/PhoneCall/TranscriptionEventsProvider';
import { TysonCoachingProvider } from '../../voice-agent/contexts/TysonCoachingContext';
import { useWebsockets } from '../../hooks';
import { AppState } from '../../models/AppState';
import { CallLogContainer } from '../CallLog';
import { CoffeeAICoachingReview } from '../CoffeeAICoachingReview';
import {
	FocusedLeadView,
	HarnessPage as FocusedViewHarness,
	isFocusedViewOn,
	shouldUseFocusedViewForQueue,
} from '../../focused-view';
import { DemosContainer } from '../Demos';
import { FollowUpsContainer } from '../FollowUps';
import { HelpContainer } from '../Help';
import { IntegrationContainer } from '../Integration';
import { LeaderboardContainer } from '../Leaderboard';
import { Login } from '../Login';
import { MainContainer } from '../MainContainer';
import { PageNotFound } from '../PageNotFound';
import { QueueContainer } from '../Queue';
import { ReportingContainer } from '../Reporting';
import { SettingsContainer } from '../Settings';
import { SlotMachinePrizes } from '../Settings/SlotMachinePrizes';
import { VoicemailContainer } from '../Voicemail';

const LazyUserSetup = React.lazy(() => import(/* webpackChunkName: "user-setup" */ '../user/UserSetup'));

interface IProps {
	history: History<any>;
}

const AllProviders = CombinedProviders([TelephonyContextProvider, TranscriptionEventsProvider, WsNotificationProvider]);

const AidaRouterBase: React.FC<IProps> = () => {
	const logger = useEventLogging();
	const userSession = useUserSession();
	const webSockets = useWebsockets();

	// When the rep has opted into Focused View, the queue experience *is*
	// Focused View, so navigations to `/queue` redirect there (see the `/queue`
	// route below). Derived from the server-backed preference mirrored onto the
	// session, so toggling the opt-in re-renders the router reactively.
	const focusedViewEnabled = isFocusedViewOn(userSession.user?.userPreferences?.coffeePreferences);

	const onRender404 = (props: RouteComponentProps<any>) => {
		return renderInMainContainer(props, <PageNotFound />);
	};

	const onRenderLogout = (props: RouteComponentProps<any>) => {
		logger.logPageView('logout');
		webSockets?.permananentlyDisconnect();
		return <Logout {...props} isAida={true} userSession={userSession} />;
	};

	const onRenderCustomerSessionStart = (props: RouteComponentProps<any, any, any>) => {
		logger.logPageView('customerSessionStart');
		return <CustomerSessionStart {...props} userSession={userSession} />;
	};

	const renderInMainContainer = (props: RouteComponentProps<any>, component: React.ReactElement<any>) => {
		return (
			<MainContainerErrorBoundary>
				<AllProviders>
					<MainContainer {...props}>{component}</MainContainer>
				</AllProviders>
			</MainContainerErrorBoundary>
		);
	};

	const onRenderRoute = (Component: React.JSXElementConstructor<any>) => (props: RouteComponentProps<any>) => {
		logger.logPageView(props.location.pathname);
		return renderInMainContainer(props, <Component />);
	};

	// Bare route: no MainContainer (app header/nav chrome), and intentionally
	// no TelephonyContextProvider so the new focused-view pipeline owns Twilio
	// device initialization without fighting the legacy context.
	const onRenderBareRoute = (Component: React.JSXElementConstructor<any>) => (props: RouteComponentProps<any>) => {
		logger.logPageView(props.location.pathname);
		return (
			<MainContainerErrorBoundary>
				<TranscriptionEventsProvider>
					<WsNotificationProvider>
						<Component />
					</WsNotificationProvider>
				</TranscriptionEventsProvider>
			</MainContainerErrorBoundary>
		);
	};

	return (
		<TysonModeProvider>
			<TysonCoachingProvider>
				{!!AppState.errorMessages && <ErrorMessagesProvider errorMessageViewModel={AppState.errorMessages} />}
				<Switch>
					<Redirect exact={true} from='/' to='/login' />

					<Route path='/customerSessionStart' render={onRenderCustomerSessionStart} />

					<RouteWithLoggedPageView path='/login'>
						<Login />
					</RouteWithLoggedPageView>

					<PrivateRoute
						exact={true}
						path='/user/voicemail'
						render={onRenderRoute(VoicemailContainer)}
						userSession={userSession}
					/>

					<Route path='/user'>
						<LazyLoader>
							<LazyUserSetup />
						</LazyLoader>
					</Route>

					<RouteWithLoggedPageView path='/customerSessionStart'>
						<CustomerSessionStart redirection='/call-log' userSession={userSession} />
					</RouteWithLoggedPageView>

					<PrivateRoute path='/help-faqs' render={onRenderRoute(HelpContainer)} userSession={userSession} />
					<PrivateRoute path='/tyson' render={_ => <Redirect to='/queue?tysonMode=true' />} userSession={userSession} />
					<PrivateRoute
						path='/queue'
						userSession={userSession}
						render={(props: RouteComponentProps<any>) => {
							// Tyson practice mode stays on the legacy queue — Focused View
							// doesn't host the practice-lead flow yet. Carry the query
							// string through so a `/queue?companyId=…` lands on that lead.
							const isTysonMode = new URLSearchParams(props.location.search).get('tysonMode') === 'true';
							if (shouldUseFocusedViewForQueue({ focusedViewEnabled, isTysonMode })) {
								return <Redirect to={`/focused-view${props.location.search}`} />;
							}
							return (
								<MainContainerErrorBoundary>
									<AllProviders>
										<MainContainer bodyContainerStyles={[bs.overflowVisible]}>
											<QueueContainer />
										</MainContainer>
									</AllProviders>
								</MainContainerErrorBoundary>
							);
						}}
					/>
					<PrivateRoute
						path='/call-log/:recentCallId?'
						render={onRenderRoute(CallLogContainer)}
						userSession={userSession}
					/>

					<PrivateRoute
						path='/demos-to-confirm'
						redirectPath='/demos'
						render={onRenderRoute(DemosContainer)}
						userSession={userSession}
					/>
					<PrivateRoute path='/demos' render={onRenderRoute(DemosContainer)} userSession={userSession} />

					<PrivateRoute path='/follow-ups' render={onRenderRoute(FollowUpsContainer)} userSession={userSession} />
					<PrivateRoute path='/leaderboard' render={onRenderRoute(LeaderboardContainer)} userSession={userSession} />
					<PrivateRoute
						path='/settings/:setting?'
						render={onRenderRoute(SettingsContainer)}
						userSession={userSession}
					/>
					<PrivateRoute path='/reporting' render={onRenderRoute(ReportingContainer)} userSession={userSession} />
					<PrivateRoute
						path='/integration/:option?/:adding?'
						render={onRenderRoute(IntegrationContainer)}
						userSession={userSession}
					/>
					<PrivateRoute path='/logout' render={onRenderLogout} userSession={userSession} />

					{userSession?.account?.features?.aida.achievementsEnabled ? (
						<PrivateRoute
							path='/slotmachine/prizes'
							render={onRenderRoute(SlotMachinePrizes)}
							userSession={userSession}
						/>
					) : null}

					<PrivateRoute
						path='/focused-view/harness'
						render={onRenderBareRoute(FocusedViewHarness)}
						userSession={userSession}
					/>
					<PrivateRoute path='/focused-view' render={onRenderBareRoute(FocusedLeadView)} userSession={userSession} />
					{/* No onLoad gate: MGR role isn't on the session synchronously, so the page gates itself. */}
					<PrivateRoute
						path='/ai-coaching-review'
						render={onRenderBareRoute(CoffeeAICoachingReview)}
						userSession={userSession}
					/>
					<PrivateRoute render={onRender404} userSession={userSession} />
				</Switch>
			</TysonCoachingProvider>
		</TysonModeProvider>
	);
};

export const AidaRouter = observer(AidaRouterBase);
